import React, { createContext, useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { usePostHog } from 'posthog-js/react'

import { Auth, AuthSuccessPayload } from '~types'
import authService from '~api/auth.service'
import useApi from '~api/api'

export interface AuthContextInterface {
  loggedIn: boolean
  firstName: string
  lastName: string
  email: string
  isVerified: boolean
  isActive: boolean
  isStaff: boolean
  isSuperuser: boolean
  token: string | undefined
  refreshToken: string | undefined
  login: (emailOrUsername: string, password: string) => Promise<boolean>
  logout: () => Promise<void>
  refresh: (token: string) => void
  resetPassword: (data: { password: string; code: string }) => Promise<string>
  requestPasswordReset: (data: { email: string }) => void
}

const BASE_AUTH_STATE: Auth = {
  loggedIn: false,
  firstName: '',
  lastName: '',
  email: '',
  isVerified: false,
  isActive: false,
  isStaff: false,
  isSuperuser: false,
  token: undefined,
  refreshToken: undefined,
}

const AuthContext = createContext<AuthContextInterface>({} as AuthContextInterface)

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const auth = useAuthProvider()
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  return useContext(AuthContext)
}

const useAuthProvider = (): AuthContextInterface => {
  const { t } = useTranslation()
  const api = useApi()
  const posthog = usePostHog()

  const [authState, setAuthState] = useState<Auth>(BASE_AUTH_STATE)

  useEffect(() => {
    if (authState.loggedIn) {
      posthog.identify(authState.email, {
        email: authState.email,
        fullName: `${authState.firstName} ${authState.lastName}`,
        isVerified: authState.isVerified,
        isActive: authState.isActive,
        isStaff: authState.isStaff,
        isSuperuser: authState.isSuperuser,
      })
    }
  }, [authState.loggedIn])

  useEffect(() => {
    const token = localStorage.getItem('token')
    const refreshToken = localStorage.getItem('refreshToken')

    if (token && refreshToken) {
      const isValid = authService.validateToken(token)
      const tokenData = authService.decodeToken(token)
      setAuthState({
        loggedIn: isValid,
        token: token,
        refreshToken: refreshToken,
        firstName: tokenData.first_name,
        lastName: tokenData.last_name,
        email: tokenData.email,
        isVerified: tokenData.is_verified,
        isActive: tokenData.is_active,
        isStaff: tokenData.staff,
        isSuperuser: tokenData.superuser,
      })
    }
  }, [])

  const refresh = (token: string) => {
    localStorage.setItem('token', token)
    setAuthState({
      ...authState,
      token,
    })
  }

  const login = async (emailOrUsername: string, password: string): Promise<boolean> => {
    const response = await api.post<AuthSuccessPayload>('/users/token/', {
      email: emailOrUsername,
      password,
    })

    if (response.status === 200 && response.data.access) {
      localStorage.setItem('token', response.data.access)
      localStorage.setItem('refreshToken', response.data.refresh)
      const tokenData = authService.decodeToken(response.data.access)
      if (tokenData.staff) {
        setAuthState({
          loggedIn: true,
          token: response.data.access,
          refreshToken: response.data.refresh,
          firstName: tokenData.first_name,
          lastName: tokenData.last_name,
          email: tokenData.email,
          isVerified: tokenData.is_verified,
          isActive: tokenData.is_active,
          isStaff: tokenData.staff,
          isSuperuser: tokenData.superuser,
        })
      }
      return true
    } else {
      return false
    }
  }

  const logout = async () => {
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    setAuthState(BASE_AUTH_STATE)
  }

  const requestPasswordReset = async (data: { email: string }) => {
    const response = await api.post('/users/request-password-reset/', data)
    if (response.status !== 200) {
      toast.error(t('Errors.resetPasswordError', "Couldn't request reset password"))
      return
    } else {
      toast.success(t('RequestPasswordResetScreen.success', 'Password reset requested.'))
      return
    }
  }

  const resetPassword = async (data: { password: string; code: string }) => {
    const response = await api.post('/users/reset-password/', data)

    if (response.status !== 200) {
      toast.error(t('Errors.resetPasswordError', "Couldn't reset password"))
      return t('Errors.resetPasswordError', "Couldn't reset password")
    } else {
      toast.success(t('ResetPasswordScreen.success', 'Password reseted successfully.'))
      return null
    }
  }

  return {
    loggedIn: authState.loggedIn,
    firstName: authState.firstName,
    lastName: authState.lastName,
    email: authState.email,
    isVerified: authState.isVerified,
    isActive: authState.isActive,
    isStaff: authState.isStaff,
    isSuperuser: authState.isSuperuser,
    token: authState.token,
    refreshToken: authState.refreshToken,
    login,
    logout,
    resetPassword,
    refresh,
    requestPasswordReset,
  } as AuthContextInterface
}
